// Video Generator Button - Phase 2: Remotion-based instant video
// Replaced HeyGen with Remotion Player for instant, reliable podcast video
// No more timeouts, no API calls — renders client-side in the browser

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Video, Download, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Player, PlayerRef } from "@remotion/player";
import {
  PodcastComposition,
  type PodcastSegmentData,
} from "@/components/remotion/PodcastComposition";
import type { PodcastResult, PodcastConfig } from "@/lib/content-studio/types";
import { Progress } from "@/components/ui/progress";

// ─── Types ──────────────────────────────────────────────────────────────────

interface VideoGeneratorButtonProps {
  podcast: PodcastResult;
  podcastConfig?: PodcastConfig;
  title?: string;
  /** Auto-start video generation on mount (kept for API compat, not used with Remotion) */
  autoTrigger?: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const FPS = 30;
const VIDEO_WIDTH = 1280;
const VIDEO_HEIGHT = 720;

/**
 * Build Remotion-compatible segment data from PodcastResult.
 * Uses actual audio durations when available for accurate avatar sync.
 * Falls back to text-length estimation for older podcasts without duration data.
 */
function buildSegments(podcast: PodcastResult): {
  segments: PodcastSegmentData[];
  totalDurationSec: number;
} {
  const rawSegments = podcast.script?.segments || [];
  const audioSegments = podcast.audioSegments || [];

  if (rawSegments.length === 0) {
    return { segments: [], totalDurationSec: 0 };
  }

  // Check if we have real duration data from audio segments
  const hasRealDurations = audioSegments.some(s => s.durationSec && s.durationSec > 0);

  let cumulativeTime = 0;
  const segments: PodcastSegmentData[] = rawSegments.map((seg, idx) => {
    let segDuration: number;

    if (hasRealDurations) {
      // Use actual audio duration for accurate avatar sync
      const audioSeg = audioSegments.find(a => a.segmentIndex === idx);
      segDuration = audioSeg?.durationSec || 5; // Default 5s if missing
    } else {
      // Fallback: estimate based on text length
      // Calculate total text length for proportional timing
      const totalTextLength = rawSegments.reduce(
        (sum, s) => sum + (s.text?.length || 0),
        0
      );
      const estimatedDurationSec = Math.max(
        rawSegments.length * 10,
        totalTextLength / 12 // ~12 chars per second of speech
      );
      const textLength = seg.text?.length || 1;
      segDuration = (textLength / Math.max(1, totalTextLength)) * estimatedDurationSec;
    }

    const startTime = cumulativeTime;
    const endTime = startTime + segDuration;
    cumulativeTime = endTime;

    return {
      speaker: seg.speaker as "host1" | "host2",
      speakerName: seg.speakerName || (seg.speaker === "host1" ? "Host 1" : "Host 2"),
      text: seg.text || "",
      hostIndex: seg.hostIndex ?? (seg.speaker === "host1" ? 0 : 1),
      startTime,
      endTime,
    };
  });

  return {
    segments,
    totalDurationSec: cumulativeTime,
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

export function VideoGeneratorButton({
  podcast,
  podcastConfig,
  title = "Podcast Video",
}: VideoGeneratorButtonProps) {
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const playerRef = useRef<PlayerRef>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const hasAudio =
    !!podcast.combinedAudioUrl ||
    !!(podcast.audioSegments?.some((s) => !!s.audioUrl));

  const hasScript = !!(podcast.script?.segments?.length);

  // Build segment data for Remotion
  const { segments, totalDurationSec } = useMemo(
    () => buildSegments(podcast),
    [podcast]
  );

  const durationInFrames = Math.max(
    FPS * 5, // minimum 5 seconds
    Math.round(totalDurationSec * FPS)
  );

  // Host names from config or podcast data
  const host1Name =
    podcastConfig?.host1Name ||
    podcast.script?.segments?.find((s) => s.speaker === "host1")
      ?.speakerName ||
    "Host 1";

  const host2Name =
    podcastConfig?.host2Name ||
    podcast.script?.segments?.find((s) => s.speaker === "host2")
      ?.speakerName ||
    "Host 2";

  // Avatar URLs from config - custom avatars take priority over preset avatars
  const host1AvatarUrl = podcastConfig?.host1CustomAvatarUrl || podcastConfig?.host1AvatarUrl || undefined;
  const host2AvatarUrl = podcastConfig?.host2CustomAvatarUrl || podcastConfig?.host2AvatarUrl || undefined;

  const compositionProps = useMemo(
    () => ({
      title: podcast.script?.title || title,
      description: podcast.script?.description,
      host1Name,
      host2Name,
      host1AvatarUrl,
      host2AvatarUrl,
      segments,
      audioUrl: podcast.combinedAudioUrl || undefined,
      totalDurationSec,
    }),
    [podcast, title, host1Name, host2Name, host1AvatarUrl, host2AvatarUrl, segments, totalDurationSec]
  );

  // Video export using MediaRecorder to capture the Player canvas
  const handleDownloadVideo = useCallback(async () => {
    if (!playerRef.current) {
      toast.error("Video player not ready");
      return;
    }

    setIsExporting(true);
    setExportProgress(0);
    chunksRef.current = [];

    try {
      // Find the canvas element inside the player
      const playerContainer = document.querySelector('[data-remotion-player]');
      const canvas = playerContainer?.querySelector('canvas');
      
      if (!canvas) {
        toast.error("Could not access video canvas");
        setIsExporting(false);
        return;
      }

      // Create a stream from the canvas
      const canvasStream = canvas.captureStream(FPS);
      
      // Try to add audio if available
      let combinedStream = canvasStream;
      let audioElement: HTMLAudioElement | null = null;
      
      if (podcast.combinedAudioUrl) {
        try {
          // Create an audio element for the recording
          audioElement = new Audio();
          audioElement.crossOrigin = "anonymous";
          audioElement.src = podcast.combinedAudioUrl;
          await audioElement.load();
          
          // Try to capture audio using Web Audio API
          const audioContext = new AudioContext();
          const source = audioContext.createMediaElementSource(audioElement);
          const destination = audioContext.createMediaStreamDestination();
          source.connect(destination);
          source.connect(audioContext.destination);
          
          // Combine video and audio streams
          const audioTracks = destination.stream.getAudioTracks();
          const videoTracks = canvasStream.getVideoTracks();
          combinedStream = new MediaStream([...videoTracks, ...audioTracks]);
        } catch (audioErr) {
          console.warn("Could not capture audio (CORS), recording video only:", audioErr);
          toast.info("Recording video only (audio unavailable due to CORS)");
        }
      }

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/webm';

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 5000000, // 5 Mbps for good quality
      });
      
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Create the video blob and download
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(podcast.script?.title || "podcast")
          .replace(/\s+/g, "-")
          .toLowerCase()}.webm`;
        a.click();
        URL.revokeObjectURL(url);
        
        setIsExporting(false);
        setExportProgress(100);
        toast.success("Video download complete!");
        
        // Clean up audio element
        if (audioElement) {
          audioElement.pause();
          audioElement.src = "";
        }
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      toast.info("Recording video... please wait while it plays through");

      // Play the video from the start
      playerRef.current.seekTo(0);
      
      // Start audio playback in sync
      if (audioElement) {
        audioElement.currentTime = 0;
        audioElement.play().catch(console.error);
      }
      
      playerRef.current.play();

      // Track progress based on frame position
      const progressInterval = setInterval(() => {
        if (playerRef.current) {
          const currentFrame = playerRef.current.getCurrentFrame();
          const progress = (currentFrame / durationInFrames) * 100;
          setExportProgress(Math.min(progress, 99));
          
          // Check if playback is complete
          if (currentFrame >= durationInFrames - 1) {
            clearInterval(progressInterval);
            setTimeout(() => {
              mediaRecorder.stop();
              if (audioElement) {
                audioElement.pause();
              }
            }, 500);
          }
        }
      }, 100);

    } catch (error) {
      console.error("Video export failed:", error);
      toast.error("Failed to export video");
      setIsExporting(false);
    }
  }, [podcast, durationInFrames]);

  // Cancel ongoing export
  const cancelExport = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (playerRef.current) {
      playerRef.current.pause();
    }
    setIsExporting(false);
    setExportProgress(0);
    chunksRef.current = [];
    toast.info("Export cancelled");
  }, []);

  if (!hasScript || segments.length === 0) {
    return null; // No script data, nothing to show
  }

  return (
    <>
      {/* Watch Video button */}
      <Button
        onClick={() => setShowVideoDialog(true)}
        disabled={!hasScript}
        variant="outline"
        size="sm"
        className="gap-1.5 h-7 text-xs bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800"
      >
        <Play className="h-3 w-3" />
        Watch Video
      </Button>

      {/* Video playback dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-purple-600" />
              {title}
            </DialogTitle>
            <DialogDescription>
              Remotion podcast video preview — instant, no waiting
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="aspect-video bg-black rounded-lg overflow-hidden" data-remotion-player>
              <Player
                ref={playerRef}
                component={PodcastComposition}
                inputProps={compositionProps}
                durationInFrames={durationInFrames}
                compositionWidth={VIDEO_WIDTH}
                compositionHeight={VIDEO_HEIGHT}
                fps={FPS}
                controls={!isExporting}
                style={{
                  width: "100%",
                  height: "100%",
                }}
                autoPlay={false}
              />
            </div>
            
            {/* Export Progress */}
            {isExporting && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Exporting video...</span>
                  <span className="font-medium">{Math.round(exportProgress)}%</span>
                </div>
                <Progress value={exportProgress} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  Video is playing through for recording. Do not close this window.
                </p>
              </div>
            )}
            
            <div className="flex gap-2">
              {isExporting ? (
                <Button
                  onClick={cancelExport}
                  variant="destructive"
                  className="flex-1"
                >
                  Cancel Export
                </Button>
              ) : (
                <Button
                  onClick={handleDownloadVideo}
                  disabled={!hasScript}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Video
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              {isExporting 
                ? "Recording in progress — the video will download automatically when complete."
                : "Click to record and download the video as WebM format."}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
